import {
  UNIFIED_SOURCE,
  ANIM_DURATION_MS,
  ANIM_FLUSH_INTERVAL_MS,
  ANIM_DEGRADE_INTERVAL_MS,
  ANIM_DEGRADE_AFTER,
  ANIM_RECOVER_AFTER,
  ANIM_SLOW_FRAME_MS,
  ANIM_FAST_FRAME_MS,
} from '../utils/map-constants.js'

// Linear easing: velocidad constante. Antes usabamos easeOutQuad (fast
// start / slow end), que en teoria se veia bien para "carro que llega a
// su destino". En la practica el WS manda posiciones cada ~500-1500ms y
// la animacion (3s) se reinicia a mitad, asi que cada enqueueBatch creaba
// una nueva animacion desde la posicion interpolada actual y reiniciaba
// el tiron de easeOutQuad. Visible como "el movimiento se reinicia":
// el bus venia desacelerando, de golpe aceleraba de nuevo. Con linear
// la velocidad es constante, no hay tirones al re-encadenar animaciones.
function ease(t) {
  return t
}

/**
 * Smooth-interpolates dynamic entity positions on the MapLibre map.
 *
 * Design:
 * - One global rAF loop for ALL animating points (no per-point timers).
 * - Throttled updateData() at ~15fps to stay within worker budget.
 * - Viewport culling: only visible points are included in the diff.
 * - Backpressure: skips frames if the worker hasn't processed the previous one.
 * - Adaptive throttle: drops to 10fps under sustained load, recovers when clear.
 * - Auto-stop: rAF loop dies when no animations are active.
 */
export class AnimationManager {
  constructor() {
    this._animations = new Map()  // monitorId → AnimState
    this._map = null
    this._rafId = null
    this._lastFlushTime = 0
    this._pendingUpdate = false   // backpressure flag — true while worker is processing

    // Adaptive throttle state
    this._flushInterval = ANIM_FLUSH_INTERVAL_MS
    this._consecutiveSlow = 0
    this._consecutiveFast = 0
    this._degraded = false

    // Pause during user interaction
    this._paused = false

    // Bound handlers for cleanup
    this._onMoveStart = null
    this._onMoveEnd = null
    this._onSourceData = null

    // Metrics (accessible via window.__animMetrics for debugging)
    this._metrics = {
      activeAnimations: 0,
      lastFrameMs: 0,
      lastDiffSize: 0,
      totalFrames: 0,
      skippedFrames: 0,
      degraded: false,
    }
    window.__animMetrics = this._metrics
  }

  init(map, sourceManager) {
    this._map = map
    // Referencia al SourceManager para validar IDs antes de updateData.
    // Sin esto, una animacion en curso de un feature que fue removido del
    // unified-source (rebuild por filter, layer toggle, cluster change,
    // o cualquier setFeatures que limpie un entityType) causa que
    // MapLibre rechace el batch entero con "GeoJSON data is not
    // compatible with updateData". El error se logea via fire('error')
    // antes de que el catch local lo capture.
    this._sourceManager = sourceManager || null

    var self = this

    // Pause animations during user zoom/pan to avoid competing with tile rendering
    this._onMoveStart = function() { self._paused = true }
    this._onMoveEnd = function() { self._paused = false }
    map.on('movestart', this._onMoveStart)
    map.on('moveend', this._onMoveEnd)

    // Backpressure: listen for worker completion
    this._onSourceData = function(e) {
      if (e.sourceId === UNIFIED_SOURCE && e.sourceDataType === 'content') {
        self._pendingUpdate = false
      }
    }
    map.on('sourcedata', this._onSourceData)
  }

  /**
   * Enqueue a batch of position updates for animation.
   * @param {Array<{monitorId, fromLng, fromLat, toLng, toLat, speed?, course?, subtitle?}>} updates
   */
  enqueueBatch(updates) {
    var now = performance.now()
    for (var i = 0; i < updates.length; i++) {
      var u = updates[i]

      // Skip if no actual movement
      if (u.fromLng === u.toLng && u.fromLat === u.toLat) continue

      var fromLng = u.fromLng
      var fromLat = u.fromLat
      var duration = ANIM_DURATION_MS

      // If already animating, start from current interpolated position (avoid jump)
      var existing = this._animations.get(u.monitorId)
      if (existing) {
        var t = Math.min((now - existing.startTime) / existing.duration, 1)
        var eased = ease(t)
        fromLng = existing.fromLng + (existing.toLng - existing.fromLng) * eased
        fromLat = existing.fromLat + (existing.toLat - existing.fromLat) * eased
        // Duracion adaptativa: cuanto tardo en llegar este update desde
        // el anterior. Si los updates llegan cada ~1s, la animacion dura
        // ~1s y el icono alcanza el target justo cuando llega el siguiente.
        // Con la duracion fija de 3s la animacion nunca alcanzaba el
        // target y cada reinicio a mitad reiniciaba el easing. Clamp a
        // [500, 5000]ms absorbe hiccups (pausas temporales o latencia alta).
        var sinceLastStart = now - existing.startTime
        duration = Math.max(500, Math.min(sinceLastStart, 5000))
      }

      this._animations.set(u.monitorId, {
        fromLng: fromLng,
        fromLat: fromLat,
        toLng: u.toLng,
        toLat: u.toLat,
        startTime: now,
        duration: duration,
        speed: u.speed,
        course: u.course,
        subtitle: u.subtitle,
      })
    }

    this._startLoop()
  }

  /**
   * Returns the current (possibly interpolated) position of a monitor.
   * Useful for the trail renderer and follow-camera.
   */
  getCurrentPosition(monitorId) {
    var anim = this._animations.get(monitorId)
    if (!anim) return null
    var now = performance.now()
    var t = Math.min((now - anim.startTime) / anim.duration, 1)
    var eased = ease(t)
    return [
      anim.fromLng + (anim.toLng - anim.fromLng) * eased,
      anim.fromLat + (anim.toLat - anim.fromLat) * eased,
    ]
  }

  /**
   * Check if a monitor is currently being animated.
   */
  isAnimating(monitorId) {
    return this._animations.has(monitorId)
  }

  /**
   * Cancel animation for a single monitor (e.g., when entity is removed).
   */
  cancel(monitorId) {
    this._animations.delete(monitorId)
  }

  /**
   * Clear all animations and reset backpressure state.
   * Call this before any operation that destroys/recreates the source
   * (style switch, cluster settings change, filter rebuild, layer toggle).
   */
  reset() {
    this._animations.clear()
    this._pendingUpdate = false
    this._consecutiveSlow = 0
    this._consecutiveFast = 0
    if (this._degraded) {
      this._degraded = false
      this._flushInterval = ANIM_FLUSH_INTERVAL_MS
    }
  }

  /**
   * Clear animations for a specific entity type.
   * Used when a layer is toggled off.
   * @param {string} entityType - e.g. 'device', 'bus'
   */
  clearForEntityType(entityType) {
    var prefix = entityType + '-'
    for (var monitorId of this._animations.keys()) {
      if (monitorId.indexOf(prefix) === 0) {
        this._animations.delete(monitorId)
      }
    }
  }

  destroy() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
    this._animations.clear()

    if (this._map) {
      if (this._onMoveStart) this._map.off('movestart', this._onMoveStart)
      if (this._onMoveEnd) this._map.off('moveend', this._onMoveEnd)
      if (this._onSourceData) this._map.off('sourcedata', this._onSourceData)
    }

    this._map = null
    window.__animMetrics = null
  }

  // ─── Private ──────────────────────────────────────────────

  _startLoop() {
    if (this._rafId !== null) return
    var self = this
    this._rafId = requestAnimationFrame(function tick(now) {
      self._tick(now)
      if (self._animations.size > 0) {
        self._rafId = requestAnimationFrame(tick)
      } else {
        self._rafId = null
      }
    })
  }

  _tick(now) {
    var tickStart = performance.now()

    // Throttle: only flush every _flushInterval ms
    if (now - this._lastFlushTime < this._flushInterval) return

    // Backpressure: skip if worker hasn't processed previous update
    if (this._pendingUpdate) {
      this._metrics.skippedFrames++
      return
    }

    // Pause during user interaction
    if (this._paused) return

    // No map — nothing to do
    if (!this._map) return

    var bounds = this._map.getBounds()
    var sw = bounds.getSouthWest()
    var ne = bounds.getNorthEast()
    var updates = []
    var completed = []
    var orphans = []

    // Snapshot de IDs validos del unified-source. Si tenemos referencia
    // al SourceManager (lo normal), usamos su Map de features in-memory
    // como verdad. Sin SourceManager (test/standalone) saltamos la
    // validacion y dependemos del catch para limpieza reactiva.
    var validIds = (this._sourceManager && this._sourceManager.features) || null

    for (var entry of this._animations) {
      var monitorId = entry[0]
      var anim = entry[1]

      // Drop orfans: feature ya no esta en el source (removido por
      // filter rebuild, layer toggle, cluster change, etc). Sin este
      // guard MapLibre rechaza el batch entero con "GeoJSON data is
      // not compatible with updateData".
      if (validIds && !validIds.has(monitorId)) {
        orphans.push(monitorId)
        continue
      }

      var t = Math.min((now - anim.startTime) / anim.duration, 1)
      var eased = ease(t)

      var lng = anim.fromLng + (anim.toLng - anim.fromLng) * eased
      var lat = anim.fromLat + (anim.toLat - anim.fromLat) * eased

      // Viewport culling: include if current OR target position is visible
      if ((lng >= sw.lng && lng <= ne.lng && lat >= sw.lat && lat <= ne.lat) ||
          (anim.toLng >= sw.lng && anim.toLng <= ne.lng && anim.toLat >= sw.lat && anim.toLat <= ne.lat)) {
        var propsToUpdate = []
        if (anim.speed != null) propsToUpdate.push({ key: 'speed', value: anim.speed })
        if (anim.course != null) propsToUpdate.push({ key: 'course', value: anim.course })
        if (anim.subtitle) propsToUpdate.push({ key: 'subtitle', value: anim.subtitle })

        // Omitir la key cuando vacía: MapLibre rechaza addOrUpdateProperties:undefined.
        // id es la string monitorId — el source usa promoteId:'monitorId'.
        var updateEntry = {
          id: monitorId,
          newGeometry: { type: 'Point', coordinates: [lng, lat] },
        }
        if (propsToUpdate.length > 0) updateEntry.addOrUpdateProperties = propsToUpdate
        updates.push(updateEntry)
      }

      if (t >= 1) completed.push(monitorId)
    }

    // Remove completed animations
    for (var c = 0; c < completed.length; c++) {
      this._animations.delete(completed[c])
    }
    // Remove orphans detectados pre-flush
    for (var o = 0; o < orphans.length; o++) {
      this._animations.delete(orphans[o])
    }

    // Flush to MapLibre
    if (updates.length > 0) {
      this._pendingUpdate = true
      try {
        var source = this._map.getSource(UNIFIED_SOURCE)
        if (source) source.updateData({ update: updates })
      } catch (e) {
        this._pendingUpdate = false
        // Causa típica: el feature fue removido del source pero su
        // animación quedó huérfana en este manager. MapLibre falla con
        // "GeoJSON data is not compatible with updateData". Limpiamos
        // SOLO las animaciones del batch fallido (no clear() global, que
        // mataba animaciones validas en curso). updates[i].id ahora es la
        // string monitorId (promoteId del source), asi que matcheamos
        // directamente contra las keys del Map de animations.
        var idsInBatch = {}
        for (var hi = 0; hi < updates.length; hi++) {
          if (updates[hi] && updates[hi].id != null) idsInBatch[updates[hi].id] = true
        }
        var toRemove = []
        var animKeys = Array.from(this._animations.keys())
        for (var ai = 0; ai < animKeys.length; ai++) {
          if (idsInBatch[animKeys[ai]]) toRemove.push(animKeys[ai])
        }
        for (var ri = 0; ri < toRemove.length; ri++) this._animations.delete(toRemove[ri])
        // Loguear una sola vez por sesión para no spammear consola.
        if (!this._updateDataErrorLogged) {
          console.warn('[anim-manager] updateData falló — limpiando animaciones huérfanas', {
            updatesCount: updates.length,
            err: e && e.message,
          })
          this._updateDataErrorLogged = true
        }
      }
    }

    this._lastFlushTime = now

    // Adaptive throttle
    var frameDuration = performance.now() - tickStart
    this._adaptThrottle(frameDuration)

    // Update metrics
    this._metrics.activeAnimations = this._animations.size
    this._metrics.lastFrameMs = Math.round(frameDuration * 100) / 100
    this._metrics.lastDiffSize = updates.length
    this._metrics.totalFrames++
    this._metrics.degraded = this._degraded
  }

  _adaptThrottle(frameDuration) {
    if (frameDuration > ANIM_SLOW_FRAME_MS) {
      this._consecutiveSlow++
      this._consecutiveFast = 0
      if (!this._degraded && this._consecutiveSlow >= ANIM_DEGRADE_AFTER) {
        this._degraded = true
        this._flushInterval = ANIM_DEGRADE_INTERVAL_MS
      }
    } else if (frameDuration < ANIM_FAST_FRAME_MS) {
      this._consecutiveFast++
      this._consecutiveSlow = 0
      if (this._degraded && this._consecutiveFast >= ANIM_RECOVER_AFTER) {
        this._degraded = false
        this._flushInterval = ANIM_FLUSH_INTERVAL_MS
      }
    }
  }
}
